import Cookies from 'js-cookie';
import { notifications } from '@mantine/notifications';
import { CSRF_TOKEN_KEY, REFRESH_TOKEN_KEY } from '../shared/constants/cookie';
import { RoutesApi } from '../shared/constants/route';

type ApiMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface ApiOptions extends Omit<RequestInit, 'method' | 'body'> {
  method?: ApiMethod;
  body?: any;
  asJson?: boolean;
}

// ----- SERVICE MAP FROM ENV -----
const serviceMap = {
  healthcareIntelligence:
    process.env.NEXT_PUBLIC_HEALTHCARE_INTELLIGENCE_API_BASE_URL,
  marketplaceCore: process.env.NEXT_PUBLIC_MARKETPLACE_CORE_API_BASE_URL,
  marketplaceApps: process.env.NEXT_PUBLIC_MARKETPLACE_APPS_API_BASE_URL,
  healthyoneTransaction:
    process.env.NEXT_PUBLIC_HEALTHYONE_TRANSACTION_API_BASE_URL,
  izmoAuth: process.env.NEXT_PUBLIC_IZMO_AUTH_API_BASE_URL,
} as const;
type ServiceName = keyof typeof serviceMap;

const notify = async (...args: any) => {
  if (typeof window === 'undefined') {
    return;
  }
  // @ts-ignore
  notifications.show(...args);
};

export function useApi() {
  const baseUrl = process.env.NEXT_PUBLIC_BASE_URL_API || 'http://kosong.com';

  const routesApi = RoutesApi();

  const buildUrl = (endpoint: string, service: ServiceName) => {
    if (endpoint.startsWith('http')) {
      return endpoint;
    }
    const prefix = serviceMap[service];
    if (!prefix) {
      throw new Error(`Unknown API service: ${service}`);
    }

    const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    if (prefix.startsWith('http')) {
      return `${prefix}${path}`;
    }
    return `${baseUrl}${prefix}${path}`;
  };

  const apiFetch = async <T>(
    url: string,
    options: ApiOptions = {}
  ): Promise<T> => {
    const headers = new Headers(options.headers || {});
    const isFormData = options.body instanceof FormData;
    const asJson = options.asJson ?? !isFormData;

    let body: BodyInit | undefined;
    if (options.body) {
      if (isFormData) {
        body = options.body;
      } else if (asJson) {
        headers.set('Content-Type', 'application/json');
        body = JSON.stringify(options.body);
      }
    }

    // if it declare this is full url or use external api which will be change here
    const finalUrl = url.startsWith('http')
      ? url
      : url.startsWith('/')
        ? `${baseUrl}${url}`
        : `${baseUrl}/${url}`;

    try {
      const resp = await fetch(finalUrl, {
        method: options.method || 'GET',
        ...options,
        headers,
        body,
        referrerPolicy: 'no-referrer', // kebutuhan healthyone, biar gak kereferer
        credentials: 'include',
      });

      // --- global error handling for 5xx only ---
      if (!resp.ok) {
        if (resp.status >= 500) {
          console.error(
            '[GLOBAL ERROR] Server error:',
            resp.status,
            finalUrl,
            resp.text()
          );

          notify({
            color: 'red',
            title: 'Server Error',
            message:
              'An internal server error occurred. Please try again later.',
          });
        }
        // skip 400/401/403 global
      }

      const contentType = resp.headers.get('content-type') || '';
      const data = contentType.includes('application/json')
        ? await resp.json().catch(() => null)
        : await resp.text();

      // try parse if have standard response
      if (
        data &&
        typeof data === 'object' &&
        ('data' in data || 'error' in data)
      ) {
        let tmpdata = null;
        let tmperror = null;
        if ('data' in data) {
          tmpdata = data.data;
        }
        if ('error' in data) {
          tmperror = data.error;
        }
        return { data: tmpdata, error: tmperror, meta: resp } as T;
      }

      return { data, error: null, meta: resp } as T;
    } catch (err: any) {
      console.error('[GLOBAL ERROR] Network error:', err);

      if (!navigator.onLine) {
        notify({
          color: 'orange',
          title: 'Offline',
          message: 'You are offline. Please check your internet connection.',
        });
      } else {
        notify({
          color: 'red',
          title: 'Network Error',
          message: 'A network error occurred. Please try again later.',
        });
      }

      return { data: null, meta: null, error: err } as T;
    }
  };

  // ============================
  // 🚀 SDK-LIKE CLIENTS HERE
  // ============================
  const buildClient = (service: ServiceName) => ({
    get: <T = any>(endpoint: string, opts: ApiOptions = {}) =>
      apiFetch<T>(buildUrl(endpoint, service), { ...opts, method: 'GET' }),

    post: <T = any>(endpoint: string, body?: any, opts: ApiOptions = {}) =>
      apiFetch<T>(buildUrl(endpoint, service), {
        ...opts,
        body,
        method: 'POST',
      }),

    put: <T = any>(endpoint: string, body?: any, opts: ApiOptions = {}) =>
      apiFetch<T>(buildUrl(endpoint, service), {
        ...opts,
        body,
        method: 'PUT',
      }),

    patch: <T = any>(endpoint: string, body?: any, opts: ApiOptions = {}) =>
      apiFetch<T>(buildUrl(endpoint, service), {
        ...opts,
        body,
        method: 'PATCH',
      }),

    delete: <T = any>(endpoint: string, opts: ApiOptions = {}) =>
      apiFetch<T>(buildUrl(endpoint, service), { ...opts, method: 'DELETE' }),
  });

  const sdkClient = {
    healthcareIntelligence: buildClient('healthcareIntelligence'),
    marketplaceCore: buildClient('marketplaceCore'),
    marketplaceApps: buildClient('marketplaceApps'),
    healthyoneTransaction: buildClient('healthyoneTransaction'),
    izmoAuth: buildClient('izmoAuth'),
  };

  return { apiFetch, buildUrl, routesApi, ...sdkClient };
}
